/**
 * What the category-research chat should TELL THE OPERATOR about an executed operation
 * (task-onvpw98566).
 *
 * ⛔ `/chat`'s outer `success` means REQUEST HANDLED, never THE OPERATION WORKED — the executed
 * path returns it unconditionally (backend-merged/routes/category-characteristics.js:4136
 * @ 446425a3aa). The page used to read that field alone, hardcode `result: { success: true }`
 * into the chat history and title the toast 'Success'. Ask it to remove a concept that is not
 * there and it congratulated you.
 *
 * ⚠️ READING `result.success` IS NOT ENOUGH, which is the trap in the obvious fix. addConcept
 * returns `{ success: TRUE, action: 'already_exists' }` after COMMIT when the concept was
 * already present — nothing written, and it says success at BOTH layers. Only `action` shows it.
 *
 * ⛔ BUT AN `action` ALLOWLIST IS THE MIRROR DEFECT, and it would look like rigour. add_equivalence,
 * add_spec_semantic and add_validation_rule return `{ success: true, … }` with NO `action` KEY AT
 * ALL — they throw on failure, so a real failure is a 500 the caller's `!response.ok` already
 * catches. An added/removed/corrected allowlist would report all three genuine successes as
 * failures. ⇒ SUCCESS UNLESS EXPLICITLY CONTRADICTED: absence of `action` is not evidence of a
 * no-op, and this function never infers one from a missing field.
 */
export type ChatOperationOutcome = {
  /** True only when the operation actually changed something. Drives the title. */
  success: boolean;
  /**
   * `no_change` is deliberately NOT folded into `failed`: nothing went wrong and nothing was
   * written, and an operator who is shown a red error for "it was already like that" learns to
   * discount red errors.
   */
  severity: 'success' | 'no_change' | 'failed';
  message: string;
};

/** Actions that report `success: true` while writing nothing. Verified against the route's dispatch. */
const NO_OP_ACTIONS = new Set(['already_exists']);

export function classifyChatOperationOutcome(result: unknown): ChatOperationOutcome {
  if (result === null || typeof result !== 'object') {
    return {
      success: false,
      severity: 'failed',
      message: 'The server did not report what the operation did, so it cannot be confirmed.',
    };
  }

  const { success, action, message } = result as Record<string, unknown>;
  // ⛔ Only a non-empty string is a usable message. The old `|| 'Operation completed successfully'`
  // fallback fired on the one return shape that omits `message` entirely
  // (CategoryCharacteristicsService.js:7822 @ 446425a3aa) and stated, verbatim, that an operation
  // which found nothing and did nothing had succeeded. Every fallback below degrades toward the
  // TRUE statement instead.
  const stated = typeof message === 'string' && message.trim() ? message.trim() : null;

  if (success === false) {
    return {
      success: false,
      severity: 'failed',
      message: stated ?? 'The operation did not complete, and nothing was changed.',
    };
  }

  if (typeof action === 'string' && NO_OP_ACTIONS.has(action)) {
    return {
      success: false,
      severity: 'no_change',
      message: stated ?? 'Nothing was changed — it was already as requested.',
    };
  }

  return { success: true, severity: 'success', message: stated ?? 'The operation completed.' };
}
